import { useRef, useState } from 'react';
import type { ReactElement } from 'react';

import { FormatierterText } from './FormatierterText';
import { Knopf } from './Knopf';

/**
 * Editor für Abstimmungstexte mit Vorschau (§12).
 *
 * §12 verlangt eine Vorschau für Smartphone und Tablet. Sie zeigt bewusst **dieselbe** Komponente,
 * die auch die Teilnehmer sehen ({@link FormatierterText}), und folgt dem eingestellten
 * Erscheinungsbild. Eine eigene Umschaltung auf Dunkel gibt es hier nicht: der Text sieht in
 * beiden Erscheinungsbildern gleich aus, und die Umschaltung stand nur im Weg.
 *
 * Die Formatierhilfen fügen nur Auszeichnung an der Cursorstelle ein. Verbindlich bereinigt wird
 * serverseitig (§12, §25.2).
 */

interface Hilfe {
  readonly beschriftung: string;
  readonly titel: string;
  /** Was vor und hinter die Auswahl gesetzt wird. */
  readonly vorher: string;
  readonly nachher: string;
  /** Text, der eingesetzt wird, wenn nichts markiert ist. */
  readonly platzhalter: string;
  /** Auszeichnung am Zeilenanfang statt um die Auswahl herum. */
  readonly zeilenweise?: boolean;
}

const HILFEN: Hilfe[] = [
  { beschriftung: 'Fett', titel: 'Fettschrift', vorher: '**', nachher: '**', platzhalter: 'Text' },
  {
    beschriftung: 'Kursiv',
    titel: 'Kursivschrift',
    vorher: '*',
    nachher: '*',
    platzhalter: 'Text',
  },
  {
    beschriftung: 'Liste',
    titel: 'Aufzählung',
    vorher: '- ',
    nachher: '',
    platzhalter: 'Punkt',
    zeilenweise: true,
  },
  {
    beschriftung: '1. Liste',
    titel: 'Nummerierte Liste',
    vorher: '1. ',
    nachher: '',
    platzhalter: 'Punkt',
    zeilenweise: true,
  },
  {
    beschriftung: 'Link',
    titel: 'Link einfügen',
    vorher: '[',
    nachher: '](https://)',
    platzhalter: 'Beschriftung',
  },
];

/** Breiten der Vorschau (§12). */
const BREITEN = { smartphone: 'max-w-[390px]', tablet: 'max-w-[768px]' } as const;
type Breite = keyof typeof BREITEN;

export function TextEditor({
  name,
  beschriftung,
  wert,
  grenze,
  onAendern,
}: {
  name: string;
  beschriftung: string;
  wert: string;
  grenze: number;
  onAendern: (wert: string) => void;
}): ReactElement {
  const feld = useRef<HTMLTextAreaElement>(null);
  const [breite, setBreite] = useState<Breite>('smartphone');

  function wendeAn(hilfe: Hilfe): void {
    const element = feld.current;
    if (element === null) {
      return;
    }

    const von = element.selectionStart;
    const bis = element.selectionEnd;
    const markiert = wert.slice(von, bis);
    const inhalt = markiert === '' ? hilfe.platzhalter : markiert;

    const eingefuegt =
      hilfe.zeilenweise === true
        ? inhalt
            .split('\n')
            .map((zeile) => `${hilfe.vorher}${zeile}`)
            .join('\n')
        : `${hilfe.vorher}${inhalt}${hilfe.nachher}`;

    onAendern(`${wert.slice(0, von)}${eingefuegt}${wert.slice(bis)}`);

    // Den eingefügten Text markieren, damit er sich sofort überschreiben lässt.
    const start = von + hilfe.vorher.length;
    window.setTimeout(() => {
      element.focus();
      element.setSelectionRange(start, start + inhalt.length);
    }, 0);
  }

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={name} className="text-sm font-medium">
        {beschriftung}
      </label>

      <div className="flex flex-wrap gap-2">
        {HILFEN.map((hilfe) => (
          <Knopf
            key={hilfe.beschriftung}
            art="sekundaer"
            title={hilfe.titel}
            className="min-h-0 px-3 py-1 text-sm"
            onClick={() => {
              wendeAn(hilfe);
            }}
          >
            {hilfe.beschriftung}
          </Knopf>
        ))}
      </div>

      <textarea
        id={name}
        name={name}
        ref={feld}
        value={wert}
        rows={8}
        maxLength={grenze}
        required
        aria-describedby={`${name}-hilfe`}
        onChange={(ereignis) => {
          onAendern(ereignis.target.value);
        }}
        className="border-rand bg-hintergrund rounded-md border px-3 py-2 font-mono text-sm"
      />

      <p id={`${name}-hilfe`} className="text-text-gedaempft text-xs">
        Erlaubt sind Absätze, <code>**fett**</code>, <code>*kursiv*</code>, Aufzählungen mit
        <code> - </code>, nummerierte Listen mit <code> 1. </code> und Links als{' '}
        <code>[Text](https://…)</code>. HTML wird nicht ausgewertet, sondern als Text angezeigt.
      </p>

      <p className="text-text-gedaempft text-xs">
        {wert.length} von {grenze} Zeichen
      </p>

      <div className="border-rand mt-2 rounded-xl border p-3">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-medium">Vorschau</h3>

          <div className="flex flex-wrap gap-2">
            <fieldset className="flex gap-1">
              <legend className="sr-only">Breite der Vorschau</legend>
              {(Object.keys(BREITEN) as Breite[]).map((wahl) => (
                <Knopf
                  key={wahl}
                  art={breite === wahl ? 'primaer' : 'sekundaer'}
                  className="min-h-0 px-3 py-1 text-xs"
                  aria-pressed={breite === wahl}
                  onClick={() => {
                    setBreite(wahl);
                  }}
                >
                  {wahl === 'smartphone' ? 'Smartphone' : 'Tablet'}
                </Knopf>
              ))}
            </fieldset>
          </div>
        </div>

        <div
          className={`bg-hintergrund text-text border-rand mx-auto rounded-lg border p-4 ${BREITEN[breite]}`}
        >
          {wert.trim() === '' ? (
            <p className="text-text-gedaempft text-sm">Noch kein Text eingegeben.</p>
          ) : (
            <FormatierterText text={wert} />
          )}
        </div>
      </div>
    </div>
  );
}
